import React, {Component} from 'react';
import Menu from "../Menu";
import PageHeader from "../PageHeader";
import Preloader from "../Preloader";
import {
    fetchStaffNotifications,
    markStaffNotificationRead,
    StaffNotificationItem,
    StaffNotificationsResponse,
} from "./idpNotifications";

type BackendItem = StaffNotificationItem;
type BackendResponse = StaffNotificationsResponse;

type UiItem = {
    id: number | string;
    date: string;
    title: string;
    alias: string;
    isRead: boolean;
    type?: string;
};

export class Notifications extends Component<any, any> {
    private loadingMore: boolean;
    private scrollHandler: any;

    constructor(props: any) {
        super(props);
        window.scrollTo(0, 0);

        this.state = {
            notifications: [] as UiItem[],
            isLoad: false,
            auth: true,
            info: '',
            error: '',

            nextCursor: null as string | null,
            hasMore: true,
        };

        this.loadingMore = false;

        this.changeName = this.changeName.bind(this);
        this.changeDate = this.changeDate.bind(this);
        this.changeEmail = this.changeEmail.bind(this);

        this.fetchFirstPage = this.fetchFirstPage.bind(this);
        this.fetchNextPage = this.fetchNextPage.bind(this);
        this.onScroll = this.onScroll.bind(this);
    }

    changeName(event: any) {
        this.setState({name: event.target.value});
    }

    changeDate(event: any) {
        this.setState({date: event.target.value});
    }

    changeEmail(event: any) {
        this.setState({email: event.target.value});
    }

    componentDidMount() {
        this.fetchFirstPage();

        // автоподгрузка при скролле
        this.scrollHandler = this.onScroll;
        window.addEventListener('scroll', this.scrollHandler, {passive: true});
    }

    componentWillUnmount() {
        if (this.scrollHandler) {
            window.removeEventListener('scroll', this.scrollHandler);
        }
    }

    formatDate(iso: string): string {
        const d = new Date(iso);
        if (Number.isNaN(d.getTime())) return '';

        return d.toLocaleString("ru-RU", {
            day: "2-digit",
            month: "2-digit",
            year: "2-digit",
            hour: "2-digit",
            minute: "2-digit",
        });
    }

    mapBackendToUi(items: BackendItem[]): UiItem[] {
        return items.map((n) => ({
            id: n.id,
            date: this.formatDate(n.createdAt),
            title: n.title ?? 'Уведомление',
            // раньше у вас item.alias — пусть будет text
            alias: n.text ?? '',
            isRead: Boolean(n.isRead),
            type: n.type ?? 'other',
        }));
    }

    async markNotificationsAsRead(items: BackendItem[]) {
        const unreadItems = items.filter((item) => !item.isRead);
        if (unreadItems.length === 0) {
            return;
        }

        const unreadIds = new Set(unreadItems.map((item) => String(item.id)));

        await Promise.all(
            unreadItems.map((item) =>
                markStaffNotificationRead(item.id).catch(() => null)
            )
        );

        this.setState((prevState: any) => ({
            notifications: prevState.notifications.map((item: UiItem) => (
                unreadIds.has(String(item.id)) ? { ...item, isRead: true } : item
            )),
        }));
    }

    async fetchFirstPage() {
        this.setState({isLoad: false, error: ''});

        try {
            const res = await fetchStaffNotifications(20);

            const data = res.data as BackendResponse;

            if (!data || data.ok !== true || !Array.isArray(data.items)) {
                throw new Error("Invalid backend response");
            }

            const uiItems = this.mapBackendToUi(data.items);

            this.setState({
                notifications: uiItems,
                isLoad: true,
                auth: true,
                info: 'Успех',
                nextCursor: data.nextCursor ?? null,
                hasMore: Boolean(data.nextCursor),
            });

            await this.markNotificationsAsRead(data.items);
        } catch (error) {
            this.setState({
                auth: false,
                isLoad: true,
                error: 'Не удалось загрузить уведомления',
            });
        }
    }

    async fetchNextPage() {
        if (this.loadingMore) return;
        if (!this.state.hasMore) return;
        if (!this.state.nextCursor) return;

        this.loadingMore = true;

        try {
            const res = await fetchStaffNotifications(20, this.state.nextCursor);

            const data = res.data as BackendResponse;

            if (!data || data.ok !== true || !Array.isArray(data.items)) {
                throw new Error("Invalid backend response");
            }

            const uiItems = this.mapBackendToUi(data.items);

            // защита от дублей
            const existing = new Set(this.state.notifications.map((x: UiItem) => String(x.id)));
            const toAdd = uiItems.filter((x) => !existing.has(String(x.id)));

            this.setState({
                notifications: [...this.state.notifications, ...toAdd],
                nextCursor: data.nextCursor ?? null,
                hasMore: Boolean(data.nextCursor),
            });

            await this.markNotificationsAsRead(data.items);
        } catch (error) {
            // если подгрузка упала — не ломаем страницу, просто остановим hasMore
            this.setState({hasMore: false});
        } finally {
            this.loadingMore = false;
        }
    }

    onScroll() {
        // подгрузка когда до низа осталось ~300px
        const scrollTop = window.scrollY || document.documentElement.scrollTop || 0;
        const viewport = window.innerHeight || document.documentElement.clientHeight || 0;
        const fullHeight = document.documentElement.scrollHeight || 0;

        if (fullHeight - (scrollTop + viewport) < 300) {
            this.fetchNextPage();
        }
    }

    renderNotifications() {
        if (this.state.notifications.length > 0) {
            return (
                <div className='pl-3 pr-3 '>
                    {this.state.notifications.map((item: UiItem) => (
                        <div key={item.id} className='chio-card mb-2 pl-3 pr-3 pt-2 pb-2'>
                            <div className='txt-middle d-flex justify-content-end'>{item.date}</div>
                            <div className='txt-middle bold'>{item.title}</div>
                            <div className='txt-middle'>{item.alias}</div>
                        </div>
                    ))}

                    {this.state.hasMore && this.state.nextCursor && (
                        <div className='d-flex justify-content-center mt-3 mb-3'>
                            <button
                                type="button"
                                className='btn btn-light'
                                onClick={this.fetchNextPage}
                                disabled={this.loadingMore}
                            >
                                {this.loadingMore ? 'Загрузка...' : 'Загрузить ещё'}
                            </button>
                        </div>
                    )}
                </div>
            );
        } else {
            return (<div className='txt-middle text-center mt-5 bold font-weight-light'>Уведомлений пока нет</div>);
        }
    }

    render() {
        if (!this.state.isLoad) {
            return (
                <Preloader></Preloader>
            );
        } else {
            return (
                <div className='main'>
                    <div className='content mx-w-content'>
                        <PageHeader
                            pageTitle='Уведомления'
                            backBtnUrl='/'/>
                        <div className='pt-header'></div>

                        {this.state.error ? (
                            <div className='txt-middle text-center mt-5 bold font-weight-light'>
                                {this.state.error}
                            </div>
                        ) : (
                            this.renderNotifications()
                        )}
                    </div>
                    <Menu></Menu>
                </div>
            );
        }
    }
}

export default Notifications;
